/**
 * THE canonical Radar demo audit — the one dataset every marketing surface
 * that shows a Radar scorecard reads from.
 *
 * This is DEMONSTRATION data, not a live or averaged customer audit, and the
 * UI that renders it says so. It is the one place the numbers live: the score,
 * the grade, the signal profile, the metric band, the trend and the verdict
 * sentence are all derived from these tool scores by the same functions the
 * product uses, so the marketing surface cannot drift from the dashboard.
 *
 * Surfaces reading this dataset (add to the list, never fork the numbers):
 *   `/`                — RadarInstrumentPanel -> RadarDemo
 *   `/platform`        — the hero scan card and the sample-dashboard artifact
 *   `/platform/start`  — RadarDemo
 *
 * It moved here from `components/home/radar-demo-data.ts` on 2026-09-04, when
 * the heuristic evaluation (F-04) caught `/` reporting B 81/100 across five
 * pillars while `/platform` reported C 56/100 across four, with Trust at 0.
 * Two hand-typed scorecards for one product read as two different products.
 * A module under `lib/radar` belongs to every route equally; one under
 * `components/home` invited the copy.
 *
 * A surface may CROP this dataset (show three pillars, hide the trend) but it
 * may never restate a number this file does not produce. Everything a
 * scorecard prints — score, grade, pillar scores, pass count, open issues,
 * action titles — has a derived export below. Type a literal and the two
 * surfaces start drifting again the same day.
 *
 * Shaped as a strong audit with ONE clear weak pillar: crawlability, content
 * and trust in the A/B bands, citations well behind them. A flawless demo has
 * nothing to sell and a catastrophic one is not credible, so the spread has to
 * carry both a result worth showing and a gap worth fixing.
 *
 * The floor was raised on 2026-08-13. The earlier set scored three tools at 0,
 * which collapsed two of the five vertices of `SignalProfile` onto the origin:
 * the polygon read as a sliver rather than a profile, and the shape is the
 * artifact this section is built around. Every tool now scores, so all five
 * pillars plot, and Citations is the visible dent instead of a missing side.
 *
 * The numbers are load-bearing on three thresholds, so check them before
 * editing: `FAILING` (50, verdict.ts) — nothing sits below it, so the verdict
 * headline names the weakest LAYER rather than counting failing checks;
 * `PASS_THRESHOLD` (70, tool-scores.ts) — three tools sit under it, so the
 * pass-rate tile reads 10/13 rather than a clean sweep; and the grade bands
 * (scoring.ts) — the mean lands in B, not A, so the demo never claims a
 * perfect result.
 *
 * These are ILLUSTRATIVE figures. They are not a measured result, not an
 * average of real audits, and there is no external source to cite. The
 * component says so on screen. Do not add a citation that implies otherwise.
 */

import { TOOLS } from '@/types/radar'
import type { ActionItem, ToolKey, ToolState } from '@/types/radar'
import { gradeFromScore } from '@/lib/radar/scoring'
import { aggregateToolScores, passingCount } from '@/lib/radar/tool-scores'
import { summariseCategories } from '@/lib/radar/categories'
import { sortActions } from '@/lib/radar/action-priority'
import type { CategorySummary } from '@/lib/radar/categories'
import type { TrendPoint } from '@/components/radar/useTrendHistory'

/** Per-tool demo scores, keyed exactly as the product keys them. */
const DEMO_SCORES: Partial<Record<ToolKey, number>> = {
  // Crawlability — 94
  crawl: 96,
  robots: 92,
  llms: 94,
  // Content — 90
  readiness: 95,
  aeo: 84,
  schemaAudit: 90,
  // Citations — 62, the weak pillar the section is selling against
  citation: 68,
  answer: 64,
  promptSov: 55,
  // Community — 72
  reddit: 74,
  sourceInfluence: 70,
  // Trust — 85
  hallucinationCheck: 88,
  brandDisambiguation: 82,
}

/** Demo scores shaped as real ToolState so product components accept them. */
export const DEMO_TOOLS: Partial<Record<ToolKey, ToolState>> =
  Object.fromEntries(
    Object.entries(DEMO_SCORES).map(([key, score]) => [
      key,
      {
        status: 'done' as const,
        data: { overallScore: score, grade: gradeFromScore(score) },
        error: null,
        startedAt: 0,
        finishedAt: 0,
      },
    ])
  )

/**
 * Deliberately generic. Showing our own domain made the demo read as a
 * Pixelmojo self-audit rather than as what the visitor's report will look
 * like, and it invited the reader to check our real numbers against a fixed
 * sample. A placeholder host keeps the artifact about them.
 */
export const DEMO_DOMAIN = 'yourdomain.com'

/**
 * 1 error + 4 warnings = 5 open issues. Lowered with the score: nine open
 * issues beside a B-band result would have described a different audit than
 * the one the tool scores describe.
 */
export const DEMO_ERROR_COUNT = 1
export const DEMO_WARNING_COUNT = 4

/**
 * The score this demo reports, derived from the tool scores above by the same
 * function the dashboard uses. Everything that shows a number reads it from
 * here, so the headline and the sparkline cannot disagree.
 */
export const DEMO_AGGREGATE = aggregateToolScores(DEMO_TOOLS)

if (DEMO_AGGREGATE.score === null) {
  // Every tool in DEMO_SCORES carries a number, so this is unreachable unless
  // the dataset above is edited into an unscorable state. Throwing beats a
  // fallback: `?? 0` would render a confident 0/100 and a sparkline dropping
  // to the floor, which is the "withheld is not zero" bug the product spent a
  // release removing.
  throw new Error(
    'DEMO_TOOLS produced no score — every demo tool must carry a number'
  )
}

export const DEMO_SCORE = DEMO_AGGREGATE.score

/**
 * Eight runs climbing to the current one.
 *
 * The LAST point is this run, so it is derived rather than typed: the
 * sparkline's endpoint is the same number the hero prints beside it. It was
 * previously hardcoded at 79 against an 81 headline, which read as a
 * contradiction — the chart appeared to disagree with the score.
 *
 * The seven before it are history, re-based when the tool scores were raised:
 * a series flat in the mid-50s under an 81 would show a 25-point jump between
 * the last two runs with nothing in the audit to explain it.
 *
 * Dates are fixed strings, never computed from `Date.now()` — a series that
 * shifts per render would hydrate differently than it server-rendered.
 */
export const DEMO_TREND: TrendPoint[] = [
  { checkedAt: '2026-05-30', overallScore: 68, grade: 'C' },
  { checkedAt: '2026-06-06', overallScore: 69, grade: 'C' },
  { checkedAt: '2026-06-13', overallScore: 72, grade: 'B' },
  { checkedAt: '2026-06-20', overallScore: 71, grade: 'B' },
  { checkedAt: '2026-06-27', overallScore: 74, grade: 'B' },
  { checkedAt: '2026-07-04', overallScore: 73, grade: 'B' },
  { checkedAt: '2026-07-11', overallScore: 77, grade: 'B' },
  {
    checkedAt: '2026-07-18',
    overallScore: DEMO_SCORE,
    grade: gradeFromScore(DEMO_SCORE),
  },
]

/**
 * The ranked actions behind the demo.
 *
 * Shaped as real `ActionItem`s so the product's own components can read them:
 * `DashboardHero` renders `sortActions(...)[0]` in its "Next best move" slot,
 * and the Recommendations tile counts this list the way the dashboard does.
 * The previous `{ impact, title }` pair was a marketing-only shape that only
 * the deleted hand-built banner could render.
 *
 * `effort` is what the hero surfaces. `impact` is hand-authored severity and
 * stays out of the headline slot by design (see DashboardHero), but it is part
 * of the type and feeds the canonical sort as a tie-break.
 */
export const DEMO_ACTIONS: ActionItem[] = [
  {
    id: 'demo-citations',
    category: 'citations',
    title: 'Analyze 8 competitor pages being cited instead',
    // The one action the hero puts above the fold, so it carries the fullest
    // `why` of the three. Kept free of derived figures: the eight pages are
    // named in the title, but the pillar score is not quoted here, or the copy
    // would go stale the moment a tool score changes.
    why: 'Ask an AI engine about this category today and it answers from pages that are not yours. Eight competitor URLs are carrying the citations, and each one is read as the neutral answer before a buyer ever reaches your site. Citations is the weakest of your five pillars, and these eight pages are the cheapest part of it to move: the claims are already ranked, already cited, and already known to the engines.',
    how: 'Open each cited page, note the claim it earns the citation for, then publish a stronger sourced version of that claim on your own domain.',
    effort: 'medium',
    impact: 'high',
    priority: 1,
    source: 'citation',
  },
  {
    id: 'demo-freshness',
    category: 'structured-data',
    title: 'Publish modification dates AI engines can read',
    why: 'Without a machine-readable modified date the engines cannot separate current pages from stale ones, and stale-looking pages lose to fresher sources.',
    how: 'Emit dateModified in the Article schema and keep it in sync with the real last edit.',
    effort: 'low',
    impact: 'medium',
    priority: 2,
    source: 'schemaAudit',
  },
  {
    id: 'demo-sov',
    category: 'sov',
    title: 'Close the mention gap with openai',
    why: 'Prompt share of voice trails the rest of the audit: the brand is named in only a minority of the prompts buyers actually ask.',
    how: 'Publish comparison pages for the prompts buyers actually type, with the criteria and the tradeoffs stated plainly.',
    effort: 'high',
    impact: 'high',
    priority: 3,
    source: 'promptSov',
  },
]

/** Wall-clock of the demo run, shown in the stat strip. */
export const DEMO_ELAPSED = '49.8s'

/* ------------------------------------------------------------------------ *
 * Derived readouts.
 *
 * Everything below is COMPUTED from the tool scores above by the functions
 * the product itself uses. Nothing here is typed by hand, which is the whole
 * point: a marketing surface that prints one of these can never disagree with
 * the dashboard, or with another marketing surface, because there is no second
 * number to get wrong.
 * ------------------------------------------------------------------------ */

/** Letter grade for `DEMO_SCORE`, via the one grade-band table. */
export const DEMO_GRADE = gradeFromScore(DEMO_SCORE)

/**
 * The five AI-readiness pillars, from the product's own category re-cut.
 *
 * FIVE, always. `/platform` used to draw four (Community was simply missing,
 * and Trust was hand-typed as 0), which described a different audit than the
 * homepage did. A surface with room for fewer bars must slice this array, not
 * retype a shorter one.
 */
export const DEMO_PILLARS: CategorySummary[] = summariseCategories(DEMO_TOOLS)

/** Tools in the full audit — the denominator on every "n/13" readout. */
export const DEMO_TOOL_COUNT = TOOLS.length

/** Tools at or above `PASS_THRESHOLD`. The "checks passed" numerator. */
export const DEMO_PASSING_COUNT = passingCount(DEMO_TOOLS)

/**
 * Open issues behind this audit: the errors plus the warnings, counted once.
 * `/platform` printed 9 against a score of 56 while the dataset described 5 —
 * two artifacts narrating two different runs.
 */
export const DEMO_OPEN_ISSUES = DEMO_ERROR_COUNT + DEMO_WARNING_COUNT

/** Actions this demo marks high-severity. Hand-authored on the action, not measured. */
export const DEMO_HIGH_IMPACT_COUNT = DEMO_ACTIONS.filter(
  a => a.impact === 'high'
).length

/**
 * The actions in the order the product ranks them.
 *
 * Never read `DEMO_ACTIONS[0]` for a "next best move": the dashboard sorts
 * before it reads, so an unsorted surface can headline a different action than
 * the product would.
 */
export const DEMO_RANKED_ACTIONS: ActionItem[] = sortActions(DEMO_ACTIONS)
